import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from "./components/ui/toaster"
import Layout from './components/Layout'
import AccessDenied from './components/AccessDenied'
import ClientsPage from './pages/ClientsPage'
import ApisPage from './pages/ApisPage'
import { Progress } from './components/ui/progress'
import { useState, useEffect } from 'react'

// Import the OIDC web component to register it
import '@umd-dit/react-auth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5000,
      refetchOnWindowFocus: false,
      placeholderData: (previousData: unknown) => previousData,
      throwOnError: (error: Error) => {
        return error?.message?.includes('401') || error?.message?.includes('403')
      }
    },
    mutations: {
      retry: 1,
      retryDelay: 1000
    }
  }
})

function PreserveQueryParamsRoute() {
  const location = useLocation()
  return <Navigate to={`/clients${location.search}`} replace />
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAccessDenied, setIsAccessDenied] = useState(false)

  // Log OIDC environment variables
  console.log('[App] OIDC Environment Variables:', {
    authority: import.meta.env.VITE_OIDC_AUTHORITY,
    clientId: import.meta.env.VITE_OIDC_CLIENT_ID,
    apiUrl: import.meta.env.VITE_API_URL,
    devmode: import.meta.env.VITE_OIDC_DEVMODE
  })

  useEffect(() => {
    const handleAccessGranted = (event: CustomEvent) => {
      const { authenticated } = event.detail
      setIsAuthenticated(authenticated)
      setIsLoading(false)
    }

    const handleAccessDenied = () => {
      setIsAccessDenied(true)
    }

    document.addEventListener('access-granted', handleAccessGranted as EventListener)
    document.addEventListener('access-denied', handleAccessDenied as EventListener)

    return () => {
      document.removeEventListener('access-granted', handleAccessGranted as EventListener)
      document.removeEventListener('access-denied', handleAccessDenied as EventListener)
    }
  }, [])

  if (isAccessDenied) {
    return <AccessDenied />
  }

  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        {/* OIDC auth component rendered at the top level */}
        <umd-auth
          api-url={import.meta.env.VITE_API_URL}
          authority={import.meta.env.VITE_OIDC_AUTHORITY}
          client-id={import.meta.env.VITE_OIDC_CLIENT_ID}
          devmode={import.meta.env.VITE_OIDC_DEVMODE}
          debug-mode={import.meta.env.DEV ? "true" : "false"}
          id="oidc-auth-component"
        ></umd-auth>

        {isLoading ? (
          <div className="fixed inset-0 flex flex-col items-center justify-center bg-background/95">
            <div className="text-2xl font-semibold mb-8">Loading OIDC Manager</div>
            <div className="w-64 mb-4">
              <Progress value={40} className="h-2" />
            </div>
            <p className="text-muted-foreground">Authenticating...</p>
          </div>
        ) : isAuthenticated ? (
          <>
            <Routes>
              <Route path="/" element={<PreserveQueryParamsRoute/>}/>
              <Route element={<Layout/>}>
                <Route path="clients" element={<ClientsPage/>}/>
                <Route path="apis" element={<ApisPage/>}/>
              </Route>
              <Route path="*" element={<Navigate to="/" replace/>}/>
            </Routes>
            <Toaster/>
          </>
        ) : null}
      </QueryClientProvider>
    </Router>
  )
}